import { createTheme, ThemeOptions } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import { FOCUS_RING } from "./tokens";

/**
 * Module augmentation: register brand-spec palette tokens so
 * `theme.palette.infrastructure` and `theme.palette.accent.purple`
 * are typed across all consumers (ui + landing).
 */
declare module "@mui/material/styles" {
  interface Palette {
    infrastructure: { main: string; light: string };
    accent: { purple: string };
  }
  interface PaletteOptions {
    infrastructure?: { main?: string; light?: string };
    accent?: { purple?: string };
  }
}

/**
 * Base MUI theme shared between src/ui and src/landing.
 * Supports both light and dark modes with system preference detection.
 *
 * Palette follows the Rusha brand identity (`docs/ui/rusha_brand_identity_design_spec.md` §6):
 *   Flow #3b82f6 (primary), Deploy #10b981 (secondary/success),
 *   Deep Space #0c0a18 (dark canvas), Infrastructure #1e3a5f (stem),
 *   Accent Purple #7c3aed (from landing SVG #7b61ff).
 *
 * Usage:
 *   import { createThemeByMode } from '@rusha/shared-theme';
 *   const theme = createThemeByMode('light'); // or 'dark'
 *
 * See .agent/skills/ui-design-system/SKILL.md for design principles.
 */

// Brand-spec color tokens (see rusha_brand_identity_design_spec.md §6).
const brand = {
  flow: "#3b82f6", // primary / info
  flowLight: "#60a5fa",
  flowDark: "#2563eb",
  deploy: "#10b981", // secondary / success
  deployLight: "#34d399",
  deployDark: "#059669",
  infrastructure: "#1e3a5f", // stems, grounding
  infrastructureLight: "#3b6ea5",
  accentPurple: "#7c3aed", // landing SVG #7b61ff mapped to brand purple
  alert: "#f43f5e", // error
  warning: "#f59e0b",
  deepSpace: "#0c0a18", // dark canvas
  cardDark: "#1e293b",
};

// Shared color tokens (same in both modes)
const colors = {
  primary: {
    main: brand.flow,
    light: brand.flowLight,
    dark: brand.flowDark,
  },
  secondary: {
    main: brand.deploy,
    light: brand.deployLight,
    dark: brand.deployDark,
  },
  error: {
    main: brand.alert,
    light: "#fca5a5",
    dark: "#be123c",
    contrastText: "#fff",
  },
  warning: {
    main: brand.warning,
    light: "#fcd34d",
    dark: "#d97706",
    contrastText: "#fff",
  },
  info: {
    main: brand.flow,
    light: brand.flowLight,
    dark: brand.flowDark,
    contrastText: "#fff",
  },
  success: {
    main: brand.deploy,
    light: brand.deployLight,
    dark: brand.deployDark,
    contrastText: "#fff",
  },
  infrastructure: {
    main: brand.infrastructure,
    light: brand.infrastructureLight,
  },
  accent: {
    purple: brand.accentPurple,
  },
};

// Grey scale adapts to mode
const grey = {
  light: {
    50: "#F9FAFB",
    100: "#F3F4F6",
    200: "#E5E7EB",
    300: "#D1D5DB",
    400: "#9CA3AF",
    500: "#6B7280",
    600: "#4B5563",
    700: "#374151",
    800: "#1F2937",
    900: "#111827",
  },
  dark: {
    50: "#F1F5F9",
    100: "#E2E8F0",
    200: "#CBD5E1",
    300: "#94A3B8",
    400: "#64748B",
    500: "#475569",
    600: "#334155",
    700: "#1E293B",
    800: "#0F172A",
    900: "#020617",
  },
};

// Light mode palette
const lightPalette = {
  mode: "light" as const,
  primary: colors.primary,
  secondary: colors.secondary,
  error: colors.error,
  warning: colors.warning,
  info: colors.info,
  success: colors.success,
  background: {
    default: "#F8FAFC",
    paper: "rgba(255, 255, 255, 0.72)", // translucent so body grid bleeds through
  },
  text: {
    primary: "#111827",
    secondary: "#4B5563",
  },
  grey: grey.light,
  divider: "rgba(17, 24, 39, 0.08)",
  infrastructure: colors.infrastructure,
  accent: colors.accent,
};

// Dark mode palette — Deep Space canvas (#0c0a18) per brand brief §6
const darkPalette = {
  mode: "dark" as const,
  primary: colors.primary,
  secondary: colors.secondary,
  error: colors.error,
  warning: colors.warning,
  info: colors.info,
  success: colors.success,
  background: {
    default: "#0f172a", // slate-900 — app shell dark canvas
    paper: "rgba(30, 41, 59, 0.72)", // translucent so body grid bleeds through
  },
  text: {
    primary: "#F1F5F9",
    secondary: "#94A3B8",
  },
  grey: grey.dark,
  divider: "rgba(255, 255, 255, 0.08)",
  infrastructure: colors.infrastructure,
  accent: colors.accent,
};

// Common typography, shape, components (same for both modes)
const commonThemeOptions: ThemeOptions = {
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontWeight: 800,
      letterSpacing: "-0.025em",
      fontSize: "2.5rem",
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      letterSpacing: "-0.025em",
      fontSize: "2rem",
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      letterSpacing: "-0.02em",
      fontSize: "1.5rem",
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.4,
    },
    subtitle1: {
      fontWeight: 500,
      fontSize: "1rem",
      lineHeight: 1.5,
    },
    subtitle2: {
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.7,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.7,
    },
    button: {
      textTransform: "none" as const,
      fontWeight: 600,
      fontSize: "0.875rem",
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.5,
    },
    overline: {
      fontFamily: '"Roboto Mono", monospace',
      fontSize: "0.75rem",
      letterSpacing: "0.5px",
      textTransform: "uppercase" as const,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "0.625rem 1.25rem",
          fontWeight: 600,
          textTransform: "none",
          transition: `all 150ms ease-out, box-shadow ${FOCUS_RING.TRANSITION}`,
          "&:active": {
            transform: "scale(0.97)",
          },
          "&.Mui-focusVisible": {
            boxShadow: `0 0 0 ${FOCUS_RING.OFFSET}px var(--mui-palette-background-paper, #fff), 0 0 0 ${FOCUS_RING.OFFSET + FOCUS_RING.WIDTH}px var(--mui-palette-primary-main, #3b82f6)`,
          },
        },
        containedPrimary: {
          background: brand.flow,
          boxShadow: `0 2px 8px ${alpha(brand.flow, 0.2)}`,
          "&:hover": {
            background: brand.flowDark,
            boxShadow: `0 4px 12px ${alpha(brand.flow, 0.3)}`,
          },
        },
        containedSecondary: {
          background: brand.deploy,
          boxShadow: `0 2px 8px ${alpha(brand.deploy, 0.2)}`,
          "&:hover": {
            background: brand.deployDark,
            boxShadow: `0 4px 12px ${alpha(brand.deploy, 0.3)}`,
          },
        },
        outlined: {
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          transition: `box-shadow ${FOCUS_RING.TRANSITION}`,
          "&.Mui-focusVisible": {
            boxShadow: `0 0 0 ${FOCUS_RING.OFFSET}px var(--mui-palette-background-paper, #fff), 0 0 0 ${FOCUS_RING.OFFSET + FOCUS_RING.WIDTH}px var(--mui-palette-primary-main, #3b82f6)`,
          },
        },
        filledPrimary: {
          background: brand.flow,
          boxShadow: `0 2px 5px ${alpha(brand.flow, 0.15)}`,
        },
        filledSecondary: {
          background: brand.deploy,
          boxShadow: `0 2px 5px ${alpha(brand.deploy, 0.15)}`,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backdropFilter: "blur(8px)",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          "@media (min-width: 600px)": {
            minHeight: "64px",
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          fontWeight: 500,
          "&:hover": {
            textDecoration: "none",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(8px)",
        },
        outlined: {
          border: `1px solid rgba(17, 24, 39, 0.08)`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backdropFilter: "blur(8px)",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          fontSize: "0.875rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "#4B5563",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 3,
        },
        bar: {
          borderRadius: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          transition: `box-shadow ${FOCUS_RING.TRANSITION}`,
          "&.Mui-focusVisible": {
            boxShadow: `0 0 0 ${FOCUS_RING.OFFSET}px var(--mui-palette-background-paper, #fff), 0 0 0 ${FOCUS_RING.OFFSET + FOCUS_RING.WIDTH}px var(--mui-palette-primary-main, #3b82f6)`,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: `background-color 150ms ease-out, box-shadow ${FOCUS_RING.TRANSITION}`,
          "&.Mui-focusVisible": {
            boxShadow: `0 0 0 ${FOCUS_RING.OFFSET}px var(--mui-palette-background-paper, #fff), 0 0 0 ${FOCUS_RING.OFFSET + FOCUS_RING.WIDTH}px var(--mui-palette-primary-main, #3b82f6)`,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: `background-color 150ms ease-out, box-shadow ${FOCUS_RING.TRANSITION}`,
          "&.Mui-focusVisible": {
            boxShadow: `0 0 0 ${FOCUS_RING.OFFSET}px var(--mui-palette-background-paper, #fff), 0 0 0 ${FOCUS_RING.OFFSET + FOCUS_RING.WIDTH}px var(--mui-palette-primary-main, #3b82f6)`,
          },
        },
      },
    },
  },
};

/**
 * Subtle grid overlay applied to the body element in both modes.
 * Matches the auth-layout grid (40px squares, ~2% opacity).
 */
const gridBackgroundImage = (isDark: boolean): string =>
  isDark
    ? "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)"
    : "linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)";

/**
 * Create a theme for the specified mode
 */
export const createThemeByMode = (mode: "light" | "dark"): ReturnType<typeof createTheme> => {
  const palette = mode === "light" ? lightPalette : darkPalette;
  const isDark = mode === "dark";

  const cssBaselineStyleOverrides = {
    "@media (prefers-reduced-motion: reduce)": {
      "*": {
        transitionDuration: "0ms !important",
        animationDuration: "0ms !important",
      },
    },
    body: {
      backgroundImage: gridBackgroundImage(isDark),
      backgroundSize: "40px 40px",
    },
  };

  const theme = createTheme({
    ...commonThemeOptions,
    palette,
    components: {
      ...commonThemeOptions.components,
      MuiCssBaseline: {
        styleOverrides: cssBaselineStyleOverrides,
      },
    },
  });

  return theme;
};

/**
 * Legacy export - creates a light theme (backward compatible)
 * @deprecated Use createThemeByMode instead
 */
export const baseTheme = createThemeByMode("light");

// Export both for convenience
export const lightTheme = createThemeByMode("light");
export const darkTheme = createThemeByMode("dark");

// Re-export PaletteMode for type safety
export type { PaletteMode } from "@mui/material";
